import { QueryKey as CacheKey } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { SPACE_FOLDER_PAGE_SIZE, SpaceKind, SpaceNodeSortBy, SpaceSearchView } from 'shared';

import { BackendRoute } from '../../../config/index';
import { QueryKey } from '../../../constants/index';
import { ApiService } from '../../../services/index';
import { selectedCompanyStorage } from '../../../storage/company/selected-company.util';
import { Order } from '../../../types/general.types';
import { SPACE_BACKEND_ROUTE } from '../constants/spaces-query.const';
import { SpaceRef } from '../types/data-room.types';
import { useBrowserPages } from './use-browser-pages.hook';
import { FolderTreeScope } from './use-space-navigation-folders.hook';

type Props = {
  // Which side of the module is reading: the owner's space routes, or the recipient's room route.
  scope?: FolderTreeScope;
  // As typed, trimmed here; an empty term searches nothing.
  search: string;
  // The column and its direction; the server sorts, so every page continues the order of the one
  // before it. The Vault's own search orders by name alone and is not sent the column.
  sortBy: SpaceNodeSortBy;
  sortOrder: Order;
  // Null where there is nothing the viewer may search.
  space: null | SpaceRef;
};

// Where the space sits in the query key; the term and the sort come after it.
const SPACE_KEY_LENGTH = 5;

// The Vault is the session company's, so its route takes no id. A recipient searches the room
// through their own route, which the access row scopes.
const searchRoute = (scope: FolderTreeScope, { id, kind }: SpaceRef) => {
  if (kind === SpaceKind.VAULT) {
    return { endpoint: BackendRoute.SPACES, routePath: ['vault', 'search'] };
  }

  if (scope === FolderTreeScope.RECIPIENT) {
    return { endpoint: BackendRoute.MY_DATA_ROOMS, routePath: [id, 'search'] };
  }

  return { endpoint: SPACE_BACKEND_ROUTE[kind], routePath: [id, 'search'] };
};

/**
 * The folders and files of a whole space whose name holds the term, paged as a folder's contents
 * are. While the term changes, the last results of the same space stay on screen until the new
 * ones arrive, so typing does not flash a skeleton.
 */
export const useSpaceSearch = ({
  scope = FolderTreeScope.OWNER,
  search,
  sortBy,
  sortOrder,
  space,
}: Props) => {
  // Only keys the cache: the routes take the company from the session.
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);
  const term = search.trim();
  const isSearching = !!space && !!companyId && !!term;
  const queryKey = useMemo(
    () => [
      QueryKey.GET_SPACE_SEARCH,
      companyId,
      scope,
      space?.kind,
      space?.id,
      term,
      sortBy,
      sortOrder,
    ],
    [companyId, scope, sortBy, sortOrder, space?.id, space?.kind, term],
  );

  const fetchPage = useCallback(
    (skip: number) => {
      if (!space) throw new Error('A space is required to search');

      return ApiService.get<SpaceSearchView>(searchRoute(scope, space), {
        queryParams: {
          search: term,
          skip,
          ...(space.kind === SpaceKind.VAULT ? {} : { sortBy }),
          sortOrder,
          take: SPACE_FOLDER_PAGE_SIZE,
        },
      });
    },
    [scope, sortBy, sortOrder, space, term],
  );

  const keepsRowsOf = useCallback(
    (previousKey: CacheKey) =>
      queryKey.slice(0, SPACE_KEY_LENGTH).every((part, index) => part === previousKey[index]),
    [queryKey],
  );

  const pages = useBrowserPages({
    enabled: isSearching,
    failureMessage: 'SPACE_SEARCH_LOAD_FAILED',
    fetchPage,
    keepsRowsOf,
    queryKey,
  });

  return { ...pages, isSearching };
};
